import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ICON_DEFS, SCENARIOS, SUBTAB_CONFIG, scenarioKey, MOVE_MS, SPIN_MS, REDUCE_MOTION } from './scenarios';
import './InfraDemo.css';

const SVG_NS = 'http://www.w3.org/2000/svg';

// Builds an icon as real SVG DOM nodes (createElementNS), not innerHTML —
// the diagram below is assembled imperatively, so icons need to be inserted
// the same way rather than via markup strings.
function buildIconSvg(name) {
  const defs = ICON_DEFS[name];
  if (!defs) return null;
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '1.5');
  defs.forEach(([tag, attrs]) => {
    const el = document.createElementNS(SVG_NS, tag);
    Object.entries(attrs).forEach(([key, value]) => el.setAttribute(key, value));
    svg.appendChild(el);
  });
  return svg;
}

const TOP_TABS = [
  { id: 'simple', label: 'Simple LLM Chatbot' },
  { id: 'assistant', label: 'AI Assistant' },
  { id: 'agent', label: 'AI Agent' }
];

// Renders one animated "How different AI systems actually talk" diagram.
// The tab/caption/chat shell below is plain React; the diagram itself is an
// imperative engine (ported from a standalone prototype) that builds nodes,
// connectors, and an animated ball directly into `trackRef` — the timing and
// geometry were tuned by hand across many iterations, so this stays a direct
// port rather than a from-scratch declarative rewrite.
export function InfraDemo() {
  const [topTab, setTopTab] = useState('simple');
  const [variant, setVariant] = useState(null);

  const trackRef = useRef(null);
  const chatLogRef = useRef(null);
  const sendBtnRef = useRef(null);
  const announceRef = useRef(null);

  const activeTimeoutRef = useRef(null);
  const mergeTimeoutRef = useRef(null);
  const cycleIntervalRef = useRef(null);
  const cycleTimeoutRef = useRef(null);
  const holdTimeoutRef = useRef(null);
  const selectTimeoutRef = useRef(null);
  const playingRef = useRef(false);

  const scenario = SCENARIOS[scenarioKey(topTab, variant)];

  // ---- Responsive node positioning ----
  // Nodes may define a `mobile: { x, y }` override used below the 800px
  // breakpoint (same breakpoint as the CSS media query).
  const isMobileView = useCallback(() => window.matchMedia('(max-width: 800px)').matches, []);
  const px = useCallback((n) => (isMobileView() && n.mobile && n.mobile.x != null ? n.mobile.x : n.x), [isMobileView]);
  const py = useCallback((n) => (isMobileView() && n.mobile && n.mobile.y != null ? n.mobile.y : n.y), [isMobileView]);

  // ---- Diagram rendering ----
  const renderDiagram = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const cfg = scenario;
    track.replaceChildren();
    const resolvedTrackHeight = isMobileView() && cfg.mobileTrackHeight ? cfg.mobileTrackHeight : cfg.trackHeight || 300;
    track.style.setProperty('--infra-track-height', resolvedTrackHeight + 'px');

    const mainNodes = Object.entries(cfg.nodes).filter(([, n]) => !n.branch);
    const xs = mainNodes.map(([, n]) => px(n));
    const minX = Math.min(...xs);
    // A scenario can define `mainLineEnd` to stop the connector line at a
    // box border instead of running it all the way to the rightmost node's
    // center (e.g. so it doesn't visually cross through a "Tool Calling" box).
    const maxX = cfg.mainLineEnd != null ? cfg.mainLineEnd : Math.max(...xs);

    const mainLine = document.createElement('div');
    mainLine.className = 'infra-connector-line';
    mainLine.style.top = '50%';
    mainLine.style.left = minX + '%';
    mainLine.style.width = maxX - minX + '%';
    track.appendChild(mainLine);

    Object.entries(cfg.nodes).forEach(([, n]) => {
      if (n.branch && !n.skipConnector) {
        const anchor = cfg.nodes[n.anchor];
        const branchLine = document.createElement('div');
        branchLine.className = 'infra-connector-branch';
        branchLine.style.left = px(n) + '%';
        branchLine.style.top = py(n) + '%';
        branchLine.style.height = py(anchor) - py(n) + '%';
        track.appendChild(branchLine);
      }
    });

    (cfg.groupBoxes || []).forEach((gb) => {
      const gxs = gb.nodes.map((id) => px(cfg.nodes[id]));
      const gys = gb.nodes.map((id) => py(cfg.nodes[id]));
      const pad = isMobileView() && gb.mobilePad ? gb.mobilePad : gb.pad || { left: 8, right: 8, top: 20, bottom: 24 };
      const left = Math.min(...gxs) - pad.left;
      const right = Math.max(...gxs) + pad.right;
      const top = Math.min(...gys) - pad.top;
      const bottom = Math.max(...gys) + pad.bottom;

      const box = document.createElement('div');
      box.className = 'infra-group-box' + (gb.style === 'solid' ? ' solid' : '');
      box.style.left = left + '%';
      box.style.top = top + '%';
      box.style.width = right - left + '%';
      box.style.height = bottom - top + '%';

      const boxLabel = document.createElement('div');
      boxLabel.className = 'infra-group-box-label' + (gb.labelCenter ? ' centered' : '');
      boxLabel.textContent = gb.label;
      box.appendChild(boxLabel);

      track.appendChild(box);
    });

    Object.entries(cfg.nodes).forEach(([id, n]) => {
      if (n.invisible) return;

      const node = document.createElement('div');
      node.className = 'infra-node';
      node.style.left = px(n) + '%';
      node.style.top = py(n) + '%';
      node.dataset.nodeId = id;

      const iconBox = document.createElement('div');
      iconBox.className = 'infra-node-icon-box' + (n.textIcon ? ' text-icon' : '');
      if (n.textIcon) {
        iconBox.textContent = n.textIcon;
      } else {
        const svg = buildIconSvg(n.icon);
        if (svg) iconBox.appendChild(svg);
      }
      node.appendChild(iconBox);

      if (n.callout) {
        const callout = document.createElement('div');
        callout.className = 'infra-callout';
        callout.id = 'infra-callout-' + id;
        if (Array.isArray(n.callout)) {
          n.callout.forEach((line) => {
            const row = document.createElement('div');
            row.className = 'infra-callout-line';
            row.textContent = line;
            callout.appendChild(row);
          });
        } else {
          callout.textContent = n.callout;
        }
        node.appendChild(callout);
      }

      const label = document.createElement('div');
      label.className = 'infra-node-label';
      label.textContent = n.label;
      node.appendChild(label);

      track.appendChild(node);
    });

    const ballAnchor = document.createElement('div');
    ballAnchor.className = 'infra-ball-anchor';
    ballAnchor.id = 'infra-ballAnchor';
    const startNode = cfg.nodes[cfg.start];
    ballAnchor.style.left = px(startNode) + '%';
    ballAnchor.style.top = py(startNode) + '%';
    const ballDot = document.createElement('span');
    ballDot.className = 'infra-ball-dot';
    ballDot.id = 'infra-ballDot';
    ballAnchor.appendChild(ballDot);
    track.appendChild(ballAnchor);

    (cfg.secondaryBalls || []).forEach((b) => {
      const bNode = cfg.nodes[b.start];
      const anchor = document.createElement('div');
      anchor.className = 'infra-ball-anchor';
      anchor.id = 'infra-ballAnchor-' + b.id;
      anchor.style.left = px(bNode) + '%';
      anchor.style.top = py(bNode) + '%';
      anchor.style.display = 'none';
      const dot = document.createElement('span');
      dot.className = 'infra-ball-dot';
      dot.id = 'infra-ballDot-' + b.id;
      anchor.appendChild(dot);
      track.appendChild(anchor);
    });
  }, [scenario, isMobileView, px, py]);

  const moveBallTo = useCallback(
    (nodeId) => {
      const n = scenario.nodes[nodeId];
      const anchor = trackRef.current?.querySelector('#infra-ballAnchor');
      if (!anchor) return;
      anchor.style.left = px(n) + '%';
      anchor.style.top = py(n) + '%';
    },
    [scenario, px, py]
  );

  const moveSecondaryBallsTo = useCallback(
    (nodeId) => {
      const n = scenario.nodes[nodeId];
      (scenario.secondaryBalls || []).forEach((b) => {
        const anchor = trackRef.current?.querySelector('#infra-ballAnchor-' + b.id);
        if (!anchor) return;
        anchor.style.left = px(n) + '%';
        anchor.style.top = py(n) + '%';
      });
    },
    [scenario, px, py]
  );

  const resetSecondaryBalls = useCallback(() => {
    (scenario.secondaryBalls || []).forEach((b) => {
      const anchor = trackRef.current?.querySelector('#infra-ballAnchor-' + b.id);
      if (!anchor) return;
      anchor.style.display = '';
      const n = scenario.nodes[b.start];
      anchor.style.left = px(n) + '%';
      anchor.style.top = py(n) + '%';
    });
  }, [scenario, px, py]);

  const hideSecondaryBalls = useCallback(() => {
    (scenario.secondaryBalls || []).forEach((b) => {
      const anchor = trackRef.current?.querySelector('#infra-ballAnchor-' + b.id);
      if (anchor) anchor.style.display = 'none';
    });
  }, [scenario]);

  const mergeBalls = useCallback(() => {
    const dot = trackRef.current?.querySelector('#infra-ballDot');
    if (dot) {
      dot.classList.remove('merging');
      void dot.offsetWidth;
      dot.classList.add('merging');
      // Cleared automatically at the start of the next step() call — see there
      // for why it can't just be a timeout (races against 'looping').
    }
    hideSecondaryBalls();
  }, [hideSecondaryBalls]);

  const spinBall = useCallback(() => {
    const dot = trackRef.current?.querySelector('#infra-ballDot');
    if (!dot) return;
    dot.classList.remove('looping');
    void dot.offsetWidth;
    dot.classList.add('looping');
  }, []);

  const hideAllCallouts = useCallback(() => {
    trackRef.current?.querySelectorAll('.infra-callout.visible').forEach((c) => c.classList.remove('visible'));
  }, []);

  const showCalloutFor = useCallback((nodeId, textOverride) => {
    const el = trackRef.current?.querySelector('#infra-callout-' + nodeId);
    if (!el) return;
    if (textOverride) {
      el.replaceChildren();
      if (Array.isArray(textOverride)) {
        textOverride.forEach((line) => {
          const row = document.createElement('div');
          row.className = 'infra-callout-line';
          row.textContent = line;
          el.appendChild(row);
        });
      } else {
        el.textContent = textOverride;
      }
    }
    el.classList.add('visible');
  }, []);

  const setNodeActive = useCallback((nodeId, active) => {
    const box = trackRef.current?.querySelector('[data-node-id="' + nodeId + '"] .infra-node-icon-box');
    if (box) box.classList.toggle('active', active);
  }, []);

  const startToolCycle = useCallback(
    (entries, totalDur) => {
      clearInterval(cycleIntervalRef.current);
      clearTimeout(cycleTimeoutRef.current);
      entries.forEach((id) => setNodeActive(id, false));
      let idx = 0;
      cycleIntervalRef.current = setInterval(() => {
        entries.forEach((id, i) => setNodeActive(id, i === idx));
        idx = (idx + 1) % entries.length;
      }, 220);
      cycleTimeoutRef.current = setTimeout(() => {
        clearInterval(cycleIntervalRef.current);
        cycleIntervalRef.current = null;
      }, Math.max(totalDur - 100, 0));
    },
    [setNodeActive]
  );

  const selectTool = useCallback(
    (entries, selectedId) => {
      clearInterval(cycleIntervalRef.current);
      cycleIntervalRef.current = null;
      entries.forEach((id) => setNodeActive(id, id === selectedId));
    },
    [setNodeActive]
  );

  // ---- Chat rendering ----
  const resetChat = useCallback(() => {
    chatLogRef.current?.replaceChildren();
  }, []);

  const addBubble = useCallback((text, who) => {
    const chatLog = chatLogRef.current;
    if (!chatLog) return;
    const b = document.createElement('div');
    b.className = 'infra-bubble ' + who;
    b.textContent = text;
    chatLog.appendChild(b);
    requestAnimationFrame(() => b.classList.add('visible'));
    chatLog.scrollTop = chatLog.scrollHeight;
  }, []);

  const clearAllActive = useCallback(() => {
    trackRef.current?.querySelectorAll('.infra-node-icon-box.active').forEach((el) => el.classList.remove('active'));
  }, []);

  const resetPlaybackState = useCallback(() => {
    clearTimeout(activeTimeoutRef.current);
    clearTimeout(mergeTimeoutRef.current);
    clearTimeout(cycleTimeoutRef.current);
    clearTimeout(holdTimeoutRef.current);
    clearTimeout(selectTimeoutRef.current);
    clearInterval(cycleIntervalRef.current);
    cycleIntervalRef.current = null;
    clearAllActive();
    playingRef.current = false;
    if (sendBtnRef.current) sendBtnRef.current.disabled = false;
  }, [clearAllActive]);

  // ---- Playback ----
  const playScenario = useCallback(() => {
    if (playingRef.current) return;
    playingRef.current = true;
    if (sendBtnRef.current) sendBtnRef.current.disabled = true;

    const cfg = scenario;
    resetChat();
    addBubble(cfg.userMessage, 'user');
    resetSecondaryBalls();
    clearAllActive();

    const path = cfg.path;
    let i = 0;

    function step() {
      if (i >= path.length) {
        addBubble(cfg.response, 'ai');
        if (announceRef.current) announceRef.current.textContent = 'AI response: ' + cfg.response;
        hideSecondaryBalls();
        clearAllActive();
        if (sendBtnRef.current) sendBtnRef.current.disabled = false;
        playingRef.current = false;
        return;
      }
      const entry = path[i];
      let dur = MOVE_MS;

      // 'merging' only needs to live long enough to play its own pulse; clear it
      // on every step transition so it can never block a later loop-spin (both
      // classes set the `animation` property, so a stale one wins over 'looping').
      const ballDotEl = trackRef.current?.querySelector('#infra-ballDot');
      if (ballDotEl) ballDotEl.classList.remove('merging');

      if (entry.type === 'move') {
        hideAllCallouts();
        moveBallTo(entry.to);
        dur = entry.duration || MOVE_MS;
        if (entry.showCallout) {
          showCalloutFor(typeof entry.showCallout === 'string' ? entry.showCallout : entry.to, entry.calloutText);
        }
      } else if (entry.type === 'spin') {
        hideAllCallouts();
        spinBall();
        dur = entry.duration || SPIN_MS;
        if (entry.showCallout) {
          showCalloutFor(typeof entry.showCallout === 'string' ? entry.showCallout : entry.at, entry.calloutText);
        }
        if (entry.cycleTools) {
          startToolCycle(entry.cycleTools.entries, dur);
          selectTimeoutRef.current = setTimeout(
            () => selectTool(entry.cycleTools.entries, entry.cycleTools.select),
            Math.max(dur - 80, 0)
          );
        }
      } else if (entry.type === 'merge') {
        hideAllCallouts();
        moveSecondaryBallsTo(entry.to);
        dur = entry.duration || MOVE_MS * 2;
        if (entry.showCallout) {
          showCalloutFor(typeof entry.showCallout === 'string' ? entry.showCallout : entry.to, entry.calloutText);
        }
        mergeTimeoutRef.current = setTimeout(mergeBalls, entry.mergeAt != null ? entry.mergeAt : MOVE_MS);
      } else if (entry.type === 'hold') {
        dur = entry.duration || 500;
        if (entry.at) {
          setNodeActive(entry.at, true);
          holdTimeoutRef.current = setTimeout(() => setNodeActive(entry.at, false), dur);
        }
      }

      i++;
      activeTimeoutRef.current = setTimeout(step, dur);
    }

    activeTimeoutRef.current = setTimeout(
      () => {
        moveBallTo(cfg.start);
        step();
      },
      REDUCE_MOTION ? 80 : 1000
    );
  }, [
    scenario,
    resetChat,
    addBubble,
    resetSecondaryBalls,
    clearAllActive,
    hideAllCallouts,
    moveBallTo,
    showCalloutFor,
    spinBall,
    startToolCycle,
    selectTool,
    moveSecondaryBallsTo,
    mergeBalls,
    setNodeActive,
    hideSecondaryBalls
  ]);

  // ---- Tab handlers ----
  function handleTopTabClick(id) {
    const list = SUBTAB_CONFIG[id];
    setTopTab(id);
    setVariant(list ? list[0].id : null);
  }

  function handleSubTabClick(id) {
    setVariant(id);
  }

  // Re-render + reset whenever the active scenario changes.
  useEffect(() => {
    resetChat();
    resetPlaybackState();
    renderDiagram();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topTab, variant]);

  // Re-render when crossing the mobile breakpoint so node positions pick up
  // (or drop) their mobile overrides. Skip while an animation is playing to
  // avoid yanking the ball mid-flight.
  useEffect(() => {
    const mobileMq = window.matchMedia('(max-width: 800px)');
    const onBreakpointChange = () => {
      if (!playingRef.current) renderDiagram();
    };
    if (mobileMq.addEventListener) mobileMq.addEventListener('change', onBreakpointChange);
    else if (mobileMq.addListener) mobileMq.addListener(onBreakpointChange);
    return () => {
      if (mobileMq.removeEventListener) mobileMq.removeEventListener('change', onBreakpointChange);
      else if (mobileMq.removeListener) mobileMq.removeListener(onBreakpointChange);
    };
  }, [renderDiagram]);

  // Clear any in-flight timers if the widget is ever unmounted mid-animation.
  useEffect(() => {
    return () => {
      clearTimeout(activeTimeoutRef.current);
      clearTimeout(mergeTimeoutRef.current);
      clearTimeout(cycleTimeoutRef.current);
      clearTimeout(holdTimeoutRef.current);
      clearTimeout(selectTimeoutRef.current);
      clearInterval(cycleIntervalRef.current);
    };
  }, []);

  const subtabList = SUBTAB_CONFIG[topTab];

  return (
    <div className="infra-demo">
      <div className="infra-tabs" role="tablist" aria-label="AI system type">
        {TOP_TABS.map((t) => (
          <button
            key={t.id}
            className={'infra-tab' + (topTab === t.id ? ' active' : '')}
            role="tab"
            aria-selected={topTab === t.id}
            onClick={() => handleTopTabClick(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {subtabList && (
        <div className="infra-subtabs">
          {subtabList.map((v) => (
            <button
              key={v.id}
              className={'infra-subtab' + (variant === v.id ? ' active' : '')}
              onClick={() => handleSubTabClick(v.id)}
            >
              {v.label}
            </button>
          ))}
        </div>
      )}

      <p className="infra-caption">{scenario.caption}</p>

      <div className="infra-widget">
        <div className="infra-chat-panel">
          <div className="infra-chat-header">Chat</div>
          <div className="infra-chat-log" ref={chatLogRef}></div>
          <div className="infra-chat-footer">
            <div className="infra-message-preview">{scenario.userMessage}</div>
            <button className="infra-send-btn" ref={sendBtnRef} onClick={playScenario}>
              Send
            </button>
          </div>
        </div>
        <div className="infra-diagram-panel">
          <div className="infra-diagram-track" ref={trackRef}></div>
        </div>
      </div>

      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only" ref={announceRef}></div>
    </div>
  );
}
